import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, FileText, List, RefreshCw, RotateCcw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';

const parseTraceNumbers = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const DocumentNumber = () => {
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const loadRows = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetTraceNumbers({});
      const list = parseTraceNumbers(response).map((item) => ({
        Id: item.Id,
        Name: item.Name || '',
        Prefix: item.Prefix ?? '',
        Number: item.Number ?? '',
      }));
      setRows(list);
      setOriginalRows(list);
    } catch (err) {
      console.error('Load trace numbers error:', err);
      setError('Failed to load document numbers');
      setRows([]);
      setOriginalRows([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const name = (row.Name || '').toLowerCase();
      const prefix = String(row.Prefix ?? '').toLowerCase();
      const number = String(row.Number ?? '').toLowerCase();
      return name.includes(term) || prefix.includes(term) || number.includes(term);
    });
  }, [rows, searchTerm]);

  const isRowChanged = (row) => {
    const original = originalRows.find((item) => item.Id === row.Id);
    if (!original) return false;
    return String(original.Prefix ?? '') !== String(row.Prefix ?? '') || String(original.Number ?? '') !== String(row.Number ?? '');
  };

  const handleFieldChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.Id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = async (row) => {
    const prefix = String(row.Prefix ?? '').trim();
    const numberValue = row.Number;
    if (prefix === '') {
      setError('Prefix is required');
      return;
    }
    if (numberValue === '' || numberValue === null || numberValue === undefined) {
      setError('Number is required');
      return;
    }

    const number = Number(numberValue);
    if (Number.isNaN(number)) {
      setError('Number must be a valid number');
      return;
    }

    try {
      setSavingId(row.Id);
      setError('');
      const response = await MainService.UpdateTraceNumber({
        Id: row.Id,
        Prefix: prefix,
        Number: number,
      });

      if (response === true || response === 'true' || response === 'Success') {
        setSuccess(`${row.Name || 'Document number'} updated successfully`);
        await loadRows();
      } else {
        setError('Failed to update document number');
      }
    } catch (err) {
      console.error('Update trace number error:', err);
      setError('Failed to update document number');
    } finally {
      setSavingId(null);
    }
  };

  const handleResetRow = (row) => {
    const original = originalRows.find((item) => item.Id === row.Id);
    if (!original) return;
    setRows((prev) => prev.map((item) => (item.Id === row.Id ? { ...original } : item)));
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <FileText className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Number</h1>
              <p className="mt-1 text-sm text-gray-500">Manage document prefixes and the next running number.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadRows}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div className="flex items-center">
            <List className="mr-2 h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Document Number List</h3>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="relative max-h-[620px] flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {filteredRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-gray-500">
              No document numbers found
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {filteredRows.map((row) => {
                const changed = isRowChanged(row);
                return (
                  <div
                    key={row.Id}
                    className={`rounded-xl border p-4 shadow-sm transition ${
                      changed ? 'border-primary/40 bg-green-50/50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="truncate text-base font-semibold text-gray-900">{row.Name}</h4>
                        <p className="mt-1 text-xs text-gray-500">ID: {row.Id}</p>
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          changed ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {changed ? 'Changed' : 'Current'}
                      </span>
                    </div>

                    <div className="mb-4 rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Preview</p>
                      <p className="mt-1 truncate text-lg font-bold text-gray-900">{`${row.Prefix || ''}${row.Number || ''}` || '-'}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">Prefix</label>
                        <input
                          type="text"
                          value={row.Prefix ?? ''}
                          onChange={(e) => handleFieldChange(row.Id, 'Prefix', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Prefix"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">Number</label>
                        <input
                          type="number"
                          value={row.Number ?? ''}
                          onChange={(e) => handleFieldChange(row.Id, 'Number', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Number"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
                      <button
                        type="button"
                        onClick={() => handleResetRow(row)}
                        disabled={savingId === row.Id || loading || !changed}
                        className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(row)}
                        disabled={savingId === row.Id || loading || !changed}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingId === row.Id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentNumber;
